import { Epsilon, Observer, Scene, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { BaseSystem, SystemOperationDefinition } from '@modules/core/src/injectable/System';
import {
  EasingFunctionType,
  EasingModeType,
  onMousewheelObservable,
  onTouchMovedObservable,
  RenderManager,
  toAngle,
  toRadian,
  ZEDCamera,
} from '@modules/standard';

import { ItemHoSystem, TourDanjiCameraAPI } from '../..';
import { onCameraViewTypeChanged, onDongLoaded, onUpdateCompassUI } from '../../observable/TourDanjiObservable';
import { CompassResponse } from '../component/response/Compass';

export type ViewDirectionType = '동' | '서' | '남' | '북' | '항공';

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class CameraRotationSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('CameraRotationSystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly scene;
  private readonly engine;
  private readonly renderManager;
  private readonly cameraAPI;
  private readonly itemHoSystem;

  private initializedRadius: number | undefined;
  private initializedBeta: number | undefined;
  private isRotatingAnimation = false;

  private isAutoRotatingCamera = false;
  private cameraAutoRotationObserver: Observer<Scene> | undefined;

  //-----------------------
  constructor({
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
    [ItemHoSystem.INJECTABLE_TOKEN]: itemHoSystem,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
  }: {
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
    [ItemHoSystem.INJECTABLE_TOKEN]: ItemHoSystem,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
  }) {
    this.scene = babylonjs.scene;
    this.engine = this.scene.getEngine();
    this.renderManager = renderManager;
    this.cameraAPI = cameraAPI;
    this.itemHoSystem = itemHoSystem;

    // First Enter Camera Rotation Animation
    // 1회성이라 System Loop에 등록하지 않습니다.
    onDongLoaded.addOnce(() => {
      const camera = this.scene.activeCamera;
      if (camera && camera instanceof ZEDCamera) {
        this.initializedRadius = camera.radius;
        this.initializedBeta = camera.beta;
      }

      this.startAutoRotation();

      onCameraViewTypeChanged.addOnce(() => {
        this.stopAutoRotation();
      });

      onTouchMovedObservable.addOnce(() => {
        this.stopAutoRotation();
      });

      onMousewheelObservable.addOnce(() => {
        this.stopAutoRotation();
      });
    });
  }

  private startAutoRotation() {
    this.renderManager.runRenderLoop();

    this.isAutoRotatingCamera = true;
    this.cameraAutoRotationObserver = this.scene.onBeforeRenderObservable.add(() => {
      const camera = this.scene.activeCamera;
      if (camera && camera instanceof ZEDCamera) {
        const deltaTime = this.engine.getDeltaTime() * 0.001;
        const delta = deltaTime * 0.05;
        camera.alpha = camera.alpha + delta;
      }
    });
  }

  private stopAutoRotation() {
    this.isAutoRotatingCamera = false;
    if (this.cameraAutoRotationObserver) this.scene.onBeforeRenderObservable.remove(this.cameraAutoRotationObserver);
    this.renderManager.stopRenderLoop();
  }

  dispose() {
    this.isRotatingAnimation = false;
    this.initializedRadius = undefined;
    this.initializedBeta = undefined;
    this.isAutoRotatingCamera = false;
  }

  public onBeforeRender = () => {
    if (onUpdateCompassUI.hasObservers()) {
      onUpdateCompassUI.notifyObservers(this.onUpdateCompass());
    }
  };

  private onUpdateCompass() {
    const camera = this.scene.activeCamera;
    if (camera && camera instanceof ZEDCamera) {
      const degrees = camera.alpha * toAngle;

      let angle = degrees % 360;
      if (angle < 0) angle += 360;

      return {
        angle: angle,
      } as CompassResponse;
    }

    return undefined;
  }

  private isTopView = false;
  public async changeNextViewDirection(): Promise<ViewDirectionType | undefined> {
    if (this.isAutoRotatingCamera) this.stopAutoRotation();

    if (this.isRotatingAnimation) return undefined;

    const camera = this.scene.activeCamera;
    if (camera && camera instanceof ZEDCamera) {
      const degree = Math.round(camera.alpha * toAngle);

      this.isRotatingAnimation = true;

      let nextAlpha: number | undefined = undefined;
      let nextBeta: number | undefined = undefined;
      let nextRadius: number | undefined = undefined;

      const targetItem = this.itemHoSystem.getTargetEntity();
      let nextTarget = targetItem?.ItemHo.hoPosition ?? Vector3.Zero();

      let seconds = 0.4;
      const direction: ViewDirectionType[] = ['동', '남', '서', '북', '항공'];
      let directionIndex = 0;
      if (!this.isTopView && (degree + 90) % 360 <= Epsilon) {
        this.isTopView = true;
        nextBeta = 0;
        nextRadius = (this.initializedRadius ?? camera.radius) + 100;
        seconds = 0.2;
        directionIndex = 4;
      } else {
        this.isTopView = false;
        const nextDegree = degree - (degree % 90) + 90;
        nextAlpha = nextDegree * toRadian;
        nextBeta = this.initializedBeta;
        nextRadius = this.initializedRadius;
        directionIndex = (nextDegree / 90) % 4;
      }

      await this.cameraAPI.moveTo(nextTarget, nextAlpha, nextBeta, nextRadius, {
        seconds: seconds,
        easingFunction: EasingFunctionType.QuarticEase,
        easingMode: EasingModeType.Out,
      });
      this.isRotatingAnimation = false;

      return direction[directionIndex];
    }

    return undefined;
  }

  // --------------------------------

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'beforeRender',
      run: this.onBeforeRender,
    },
  ];
}
